import { ComponentProps } from "react";
import { VariantProps, cva } from "class-variance-authority";
import { cn } from "../../../utils/cn";

const TENANT_BG_CLASSES = {
  starcheck: "bg-[#266BF0]",
  log: "bg-[#50D05D]",
  vlx: "bg-[#1C1C1C]",
  tokyo: "bg-[#E42E30]",
} as const;

type Tenant = keyof typeof TENANT_BG_CLASSES;

const buttonVariants = cva(
  "min-w-[128px] h-11 w-fit px-5 rounded-[100px] flex justify-center items-center gap-2 font-['Mulish'] text-base font-semibold whitespace-nowrap leading-6 text-center disabled:cursor-not-allowed disabled:bg-[#b8b8b8]",
  {
    variants: {
      variant: {
        solid: "text-white",
        danger: "bg-[#ed0000] text-white",
        text: "bg-transparent shadow-none text-[rgba(32,51,47,0.56)] font-normal",
        underline:
          "bg-transparent shadow-none text-[rgba(32,51,47,0.56)] font-normal underline",
        outline:
          "px-5 border border-solid border-[#2d2d2d] bg-transparent shadow-none text-[#2d2d2d]",
        filter:
          "h-fit p-[5px] bg-transparent text-[#9d9d9d] text-base font-normal leading-5",
      },
    },
    defaultVariants: {
      variant: "solid",
    },
  },
);

interface IButtonV2CommonProps extends VariantProps<typeof buttonVariants> {
  color?: Tenant;
}

type IButtonV2Props =
  | (ComponentProps<"button"> & IButtonV2CommonProps & { href?: undefined })
  | (Omit<ComponentProps<"a">, "color"> &
      IButtonV2CommonProps & { href: string });

export const ButtonV2 = ({
  className,
  variant,
  color,
  href,
  ...rest
}: IButtonV2Props) => {
  const tenant = color ?? (process.env.REACT_APP_PROJECT as Tenant);
  const isSolid = (variant ?? "solid") === "solid";
  const classes = cn(
    buttonVariants({ variant }),
    isSolid && TENANT_BG_CLASSES[tenant],
    className,
  );

  if (href) {
    return (
      <a href={href} className={classes} {...(rest as ComponentProps<"a">)} />
    );
  }

  return <button className={classes} {...(rest as ComponentProps<"button">)} />;
};
